/**
 * Round cat faces for the customers and the shop, as SVG: the title screen's kitten's face,
 * in each one's fur and markings and dressed up with what they wear. The same picture is
 * shown as an image on the page and drawn on the delivery slip's canvas.
 */

/** How a face's fur is marked. */
export type Pattern =
  /** One colour all over. */
  | "plain"
  /** Stripes on the forehead and the cheeks. */
  | "tabby"
  /** Two patches of colour, over one ear each. */
  | "calico"
  /** Darker ears and muzzle, as a Siamese. */
  | "points"
  /** Dark all over but a white face parted from the forehead down (ハチワレ). */
  | "hachiware";

/** What a face wears, each drawn over it in its place. */
export type Wear =
  | "hachimaki"
  | "beanie"
  | "flower"
  | "huntingCap"
  | "bowTie"
  | "strawHat"
  | "mustache"
  | "earmuffs"
  | "chefHat"
  /** A long white beard, and brows to go with it. */
  | "beard"
  | "glasses"
  /** Pushed up on the head, over the brow. */
  | "sunglasses";

export interface Face {
  fur: string;
  pattern: Pattern;
  /** The pattern's colours: the stripes, points or dark fur; two for calico. */
  marks?: string[];
  /** The eyes' colour, if not the usual dark brown. */
  eyes?: string;
  /** The whiskers' colour, if not the usual brown (paler on pale fur). */
  whiskers?: string;
  wear: Wear[];
  /** The backdrop behind the face: round, unless `square`. */
  backdrop: string;
  /** The backdrop fills the whole square, with no round edge, as an ID photo's does. */
  square?: boolean;
}

/** How a face looks: as usual, smiling, or overjoyed. */
export type Mood = "calm" | "glad" | "delighted";

const INK = "#4a3530", EAR = "#f7c4bb", BLUSH = "#f28b82";
/** The whiskers where they are not changed. */
const WHISKERS = "#b98062";
/** A faint edge round the head and ears, so pale fur still stands off a pale backdrop. */
const EDGE = `stroke="rgba(90,60,40,.22)" stroke-width="2"`;
/** Where the lines on a face are round-ended and joined. */
const ROUND = `fill="none" stroke-linecap="round" stroke-linejoin="round"`;

/**
 * The face and what it wears are drawn in the title kitten's own frame (the head a circle of
 * radius 52 round 100, 94, the ears up to y 24), set smaller and lower in the round picture
 * to leave room above the ears for a hat and below the chin for a beard or a bow tie.
 */
const HEAD = `translate(100 121) scale(.95) translate(-100 -94)`;

const LEFT_EAR = "M56 72 L50 24 L92 50 Z", RIGHT_EAR = "M144 72 L150 24 L108 50 Z";
const LEFT_INNER = "M61 63 L57 37 L80 52 Z", RIGHT_INNER = "M139 63 L143 37 L120 52 Z";

function ears(f: Face): string {
  const [a, b] = f.pattern === "calico"
    ? [f.marks![0], f.marks![1] ?? f.marks![0]]
    : f.pattern === "points" || f.pattern === "hachiware"
    ? [f.marks![0], f.marks![0]]
    : [f.fur, f.fur];
  return `<path d="${LEFT_EAR}" fill="${a}" ${EDGE}/><path d="${RIGHT_EAR}" fill="${b}" ${EDGE}/>` +
    `<path d="${LEFT_INNER}" fill="${EAR}"/><path d="${RIGHT_INNER}" fill="${EAR}"/>`;
}

/** The markings over the head, clipped to it. */
function markings(f: Face): string {
  const [m, n] = f.marks ?? [];
  switch (f.pattern) {
    case "tabby":
      return `<g stroke="${m}" stroke-width="4.5" ${ROUND}>` +
        `<path d="M91 46 v10 M100 44 v12 M109 46 v10"/>` +
        `<path d="M49 86 h10 M48 95 h9 M151 86 h-10 M152 95 h-9"/></g>`;
    case "calico":
      return `<circle cx="64" cy="58" r="30" fill="${m}"/>` +
        `<circle cx="142" cy="66" r="22" fill="${n ?? m}"/>`;
    case "points":
      return `<ellipse cx="100" cy="106" rx="22" ry="16" fill="${m}" opacity=".55"/>`;
    case "hachiware":
      // The dark fur is the head itself; the white face over it parts at the forehead.
      return `<path d="M100 52 C92 70 60 80 46 108 L40 160 L160 160 L154 108 C140 80 108 70 100 52 Z" fill="${f.fur}"/>`;
    case "plain":
      return "";
  }
}

function eyes(f: Face, mood: Mood): string {
  const iris = f.eyes ?? INK;
  if (mood === "glad") {
    return `<g stroke="${INK}" stroke-width="4" ${ROUND}>` +
      `<path d="M72 95 Q80 84 88 95"/><path d="M112 95 Q120 84 128 95"/></g>`;
  }
  if (mood === "delighted") {
    // Wide and shining.
    return `<ellipse cx="80" cy="91" rx="7.5" ry="9" fill="${iris}"/>` +
      `<ellipse cx="120" cy="91" rx="7.5" ry="9" fill="${iris}"/>` +
      `<circle cx="83" cy="87" r="3" fill="#fff"/><circle cx="123" cy="87" r="3" fill="#fff"/>` +
      `<circle cx="77.5" cy="95" r="1.5" fill="#fff"/><circle cx="117.5" cy="95" r="1.5" fill="#fff"/>`;
  }
  return `<ellipse cx="80" cy="92" rx="5.5" ry="7" fill="${iris}"/>` +
    `<ellipse cx="120" cy="92" rx="5.5" ry="7" fill="${iris}"/>` +
    `<circle cx="82" cy="89" r="1.8" fill="#fff"/><circle cx="122" cy="89" r="1.8" fill="#fff"/>`;
}

function mouth(mood: Mood): string {
  if (mood === "delighted") {
    return `<path d="M93 106 Q100 110 107 106 Q106 118 100 118 Q94 118 93 106 Z" fill="#b5524a"/>` +
      `<path d="M96 114 Q100 111 104 114 Q102 117.5 100 117.5 Q98 117.5 96 114 Z" fill="#f28b82"/>`;
  }
  return `<path d="M94 106 q3 4 6 0 q3 4 6 0" stroke="${INK}" stroke-width="2.5" ${ROUND}/>`;
}

/** Four-pointed twinkles round an overjoyed face. */
const SPARKLES = [[30, 56, 1], [170, 40, 0.8], [178, 142, 0.6]].map(([x, y, s]) =>
  `<path transform="translate(${x} ${y}) scale(${s})" d="M0 -12 Q2 -2 12 0 Q2 2 0 12 Q-2 2 -12 0 Q-2 -2 0 -12 Z" fill="#f7c948"/>`
).join("");

/** What each thing worn looks like, drawn with the face (`face`), over the head as a hat (`hat`), or last of all, over the ears (`top`). */
const WEAR: Record<Wear, { face?: string; hat?: string; top?: string; earsOver?: boolean }> = {
  hachimaki: {
    face:
      `<path d="M44 76 Q100 58 156 76 L156 88 Q100 70 44 88 Z" fill="#fbfaf6" stroke="#cdc3b4" stroke-width="1.5" clip-path="url(#head)"/>`,
    hat: `<g fill="#fbfaf6" stroke="#cdc3b4" stroke-width="1.5" stroke-linejoin="round">` +
      `<path d="M148 76 l18 -10 l2 13 z"/><path d="M148 81 l15 12 l-10 5 z"/>` +
      `<circle cx="150" cy="79" r="5"/></g>`,
  },
  beanie: {
    hat: `<path d="M46 72 Q46 20 100 18 Q154 20 154 72 Z" fill="#d9584f"/>` +
      `<rect x="43" y="62" width="114" height="15" rx="7.5" fill="#bf473f"/>` +
      `<path d="M58 64 v11 M72 63 v12 M86 62 v13 M100 62 v13 M114 62 v13 M128 63 v12 M142 64 v11" stroke="#a83d36" stroke-width="2" stroke-linecap="round"/>`,
    earsOver: true,
    top: `<circle cx="100" cy="15" r="12" fill="#f6efe6" stroke="#e0d6c8" stroke-width="2"/>`,
  },
  flower: {
    top: `<g transform="translate(60 64)">` +
      [0, 72, 144, 216, 288].map((a) =>
        `<circle cx="${(7 * Math.sin(a * Math.PI / 180)).toFixed(2)}" cy="${
          (-7 * Math.cos(a * Math.PI / 180)).toFixed(2)
        }" r="6.5" fill="#f59bb0"/>`
      ).join("") +
      `<circle r="4.5" fill="#f7d35c"/></g>`,
  },
  huntingCap: {
    hat: `<path d="M52 62 Q54 26 100 24 Q146 26 148 62 Z" fill="#8b6a4f"/>` +
      `<path d="M66 60 Q70 36 100 30 M134 60 Q130 36 100 30" stroke="#7a5b43" stroke-width="2" fill="none"/>` +
      `<path d="M44 62 Q100 50 156 62 Q152 76 100 74 Q48 76 44 62 Z" fill="#6f5440"/>` +
      `<circle cx="100" cy="27" r="4.5" fill="#6f5440"/>`,
  },
  bowTie: {
    face:
      `<path d="M100 154 L79 143 Q74 154 79 165 Z M100 154 L121 143 Q126 154 121 165 Z" fill="#9a3140"/>` +
      `<rect x="94.5" y="147.5" width="11" height="13" rx="3.5" fill="#7d2533"/>`,
  },
  strawHat: {
    hat:
      `<ellipse cx="100" cy="58" rx="80" ry="15" fill="#eccb72" stroke="#c9a64e" stroke-width="2"/>`,
    earsOver: true,
    top:
      `<path d="M66 58 Q66 16 100 14 Q134 16 134 58 Q100 64 66 58 Z" fill="#f1d57f" stroke="#c9a64e" stroke-width="2"/>` +
      `<path d="M67 44 Q100 38 133 44 L134 55 Q100 60 66 55 Z" fill="#5fae7a"/>`,
  },
  mustache: {
    face: `<g fill="#4d4448">` +
      `<path d="M100 104 Q88 100 78 108 Q70 114 62 106 Q64 120 80 116 Q92 112 100 108 Z"/>` +
      `<path d="M100 104 Q112 100 122 108 Q130 114 138 106 Q136 120 120 116 Q108 112 100 108 Z"/></g>`,
  },
  earmuffs: {
    top:
      `<path d="M56 50 Q100 -4 144 50" stroke="#e58aa0" stroke-width="7" fill="none" stroke-linecap="round"/>` +
      `<circle cx="58" cy="54" r="19" fill="#fbe3ea" stroke="#e58aa0" stroke-width="3"/>` +
      `<circle cx="142" cy="54" r="19" fill="#fbe3ea" stroke="#e58aa0" stroke-width="3"/>`,
  },
  chefHat: {
    hat: `<g fill="#fff" stroke="#d8d2ca" stroke-width="2">` +
      `<path d="M66 44 Q50 20 72 8 Q80 -12 100 -8 Q120 -12 128 8 Q150 20 134 44 Z"/>` +
      `<rect x="64" y="36" width="72" height="20" rx="5"/></g>`,
  },
  beard: {
    face:
      `<path d="M60 116 Q60 172 100 182 Q140 172 140 116 Q124 130 100 126 Q76 130 60 116 Z" fill="#f5f2ec" stroke="#d8d0c4" stroke-width="2"/>` +
      `<path d="M64 82 Q76 70 91 78 M136 82 Q124 70 109 78" stroke="#f5f2ec" stroke-width="7" ${ROUND}/>`,
  },
  glasses: {
    face: `<g stroke="#5a4636" stroke-width="3" fill="rgba(255,255,255,.18)">` +
      `<circle cx="80" cy="92" r="13"/><circle cx="120" cy="92" r="13"/></g>` +
      `<path d="M93 91 q7 -5 14 0 M67 89 L50 85 M133 89 L150 85" stroke="#5a4636" stroke-width="3" ${ROUND}/>`,
  },
  sunglasses: {
    hat: `<g fill="#3b3346" stroke="#b8336a" stroke-width="3.5" stroke-linejoin="round">` +
      `<path d="M64 50 Q80 46 95 50 Q95 65 80 66 Q66 65 64 50 Z"/>` +
      `<path d="M136 50 Q120 46 105 50 Q105 65 120 66 Q134 65 136 50 Z"/></g>` +
      `<path d="M95 52 Q100 48 105 52 M64 51 L53 56 M136 51 L147 56" stroke="#b8336a" stroke-width="3.5" ${ROUND}/>` +
      `<path d="M70 54 l7 -2.5 M111 54 l7 -2.5" stroke="#fff" stroke-opacity=".55" stroke-width="2.5" stroke-linecap="round"/>`,
  },
};

/** The face as a standalone SVG document, 200 units across. */
export function faceSvg(f: Face, mood: Mood = "calm"): string {
  const worn = f.wear.map((w) => WEAR[w]);
  const blush = mood === "calm" ? 0.45 : mood === "glad" ? 0.55 : 0.7;
  const head = f.pattern === "hachiware" ? f.marks![0] : f.fur;
  const body = [
    ears(f),
    `<circle cx="100" cy="94" r="52" fill="${head}" ${EDGE}/>`,
    `<g clip-path="url(#head)">${markings(f)}</g>`,
    eyes(f, mood),
    `<ellipse cx="66" cy="108" rx="9" ry="5" fill="${BLUSH}" opacity="${blush}"/>`,
    `<ellipse cx="134" cy="108" rx="9" ry="5" fill="${BLUSH}" opacity="${blush}"/>`,
    mouth(mood),
    `<g stroke="${f.whiskers ?? WHISKERS}" stroke-width="2" stroke-linecap="round">` +
    `<path d="M58 102 h-20 M58 108 l-18 4 M142 102 h20 M142 108 l18 4"/></g>`,
    ...worn.map((w) => w.face ?? ""),
    ...worn.map((w) => w.hat ?? ""),
    worn.some((w) => w.earsOver) ? ears(f) : "",
    ...worn.map((w) => w.top ?? ""),
  ].join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">` +
    `<defs><clipPath id="round"><circle cx="100" cy="100" r="98"/></clipPath>` +
    `<clipPath id="head"><circle cx="100" cy="94" r="52"/></clipPath></defs>` +
    (f.square
      ? `<g><rect width="200" height="200" fill="${f.backdrop}"/>`
      : `<g clip-path="url(#round)"><circle cx="100" cy="100" r="98" fill="${f.backdrop}"/>`) +
    `<g transform="${HEAD}">${body}</g>` +
    (mood === "delighted" ? SPARKLES : "") +
    `</g>` +
    (f.square
      ? ""
      : `<circle cx="100" cy="100" r="96.5" fill="none" stroke="rgba(90,60,40,.18)" stroke-width="3"/>`) +
    `</svg>`;
}

const urls = new Map<string, string>();

/** The face as a data URL, for an image on the page; made once for each face and mood. */
export function faceUrl(f: Face, mood: Mood = "calm"): string {
  const key = JSON.stringify([f, mood]);
  let url = urls.get(key);
  if (!url) {
    url = `data:image/svg+xml,${encodeURIComponent(faceSvg(f, mood))}`;
    urls.set(key, url);
  }
  return url;
}

/** The face loaded as an image, to draw on a canvas. */
export async function faceImage(f: Face, mood: Mood = "calm"): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = faceUrl(f, mood);
  await image.decode();
  return image;
}
